/**
 * Implementação do Repositório de Tasks usando Supabase
 * Refatorado: Queries extraídas para queries/TaskQueries.ts
 */
import { supabase } from '../../lib/supabase';
import { ITaskRepository } from '../../domain/repositories/ITaskRepository';
import { Task } from '../../domain/entities/Task';
import { InfrastructureError } from '../../core/errors/DomainErrors';
import { TaskMapper } from '../mappers/TaskMapper';
import {
    baseTaskQuery,
    pendingTasksQuery,
    overdueTasksQuery,
    dueTodayTasksQuery,
    dueSoonTasksQuery,
    countPendingQuery,
    countOverdueQuery
} from './queries/TaskQueries';

export class SupabaseTaskRepository implements ITaskRepository {
    private async executeQuery(
        queryBuilder: any,
        organizationId?: string,
    ): Promise<Task[]> {
        if (organizationId) {
            queryBuilder = queryBuilder.eq('organization_id', organizationId);
        }

        const { data, error } = await queryBuilder;

        if (error) {
            console.error('[REPOSITORY] Query error:', error);
            return [];
        }

        return data.map((d: any) => TaskMapper.toEntity(d));
    }

    async findAll(organizationId?: string): Promise<Task[]> {
        let query = baseTaskQuery();

        if (organizationId) {
            query = query.eq('organization_id', organizationId);
        }

        const { data, error } = await query;

        if (error) {
            console.error('[REPOSITORY] Error fetching tasks:', error);
            throw new InfrastructureError(`Erro ao buscar tarefas: ${error.message}`, error);
        }

        return data.map(TaskMapper.toEntity);
    }

    async findById(id: string): Promise<Task | null> {
        const { data, error } = await supabase
            .from('tasks')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null;
            }
            console.error('[REPOSITORY] Error fetching task:', error);
            throw new InfrastructureError(`Erro ao buscar tarefa: ${error.message}`, error);
        }

        return TaskMapper.toEntity(data);
    }

    async findByLeadId(leadId: string): Promise<Task[]> {
        const { data, error } = await supabase
            .from('tasks')
            .select(`
                *,
                opportunities:opportunity_id (
                    id, 
                    title, 
                    contacts (
                        name, 
                        phone, 
                        email
                    )
                ), 
                profiles!assigned_to (
                    id, 
                    full_name, 
                    avatar_url
                )
            `)
            .eq('opportunity_id', leadId)
            .order('due_date', { ascending: true });

        if (error) {
            console.error('[REPOSITORY] Error fetching tasks by lead:', error);
            return [];
        }

        return data.map(TaskMapper.toEntity);
    }

    async findPending(organizationId?: string): Promise<Task[]> {
        return this.executeQuery(pendingTasksQuery(), organizationId);
    }

    async findOverdue(organizationId?: string): Promise<Task[]> {
        return this.executeQuery(overdueTasksQuery(), organizationId);
    }

    async findDueToday(organizationId?: string): Promise<Task[]> {
        return this.executeQuery(dueTodayTasksQuery(), organizationId);
    }

    async findDueSoon(organizationId?: string): Promise<Task[]> {
        return this.executeQuery(dueSoonTasksQuery(), organizationId);
    }

    async save(task: Task, organizationId?: string): Promise<Task> {
        const dbTask = TaskMapper.toDatabase(task);
        const isNew = task.id.startsWith('temp_');

        if (isNew) {
            const { id, ...taskWithoutId } = dbTask;

            const taskToInsert = {
                ...taskWithoutId,
                ...(organizationId ? { organization_id: organizationId } : {})
            };

            const { data, error } = await supabase
                .from('tasks')
                .insert([taskToInsert])
                .select('*, opportunities:opportunity_id (id, title), profiles!assigned_to (id, full_name, avatar_url)')
                .single();

            if (error) {
                console.error('[REPOSITORY] Error creating task:', error);
                throw new InfrastructureError(`Erro ao criar tarefa: ${error.message}`, error);
            }

            return TaskMapper.toEntity(data);
        } else {
            const { data, error } = await supabase
                .from('tasks')
                .update(dbTask)
                .eq('id', task.id)
                .select('*, opportunities:opportunity_id (id, title), profiles!assigned_to (id, full_name, avatar_url)')
                .single();

            if (error) {
                console.error('[REPOSITORY] Error updating task:', error);
                throw new InfrastructureError(`Erro ao atualizar tarefa: ${error.message}`, error);
            }

            return TaskMapper.toEntity(data);
        }
    }

    async delete(id: string): Promise<void> {
        const { error } = await supabase
            .from('tasks')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('[REPOSITORY] Error deleting task:', error);
            throw new InfrastructureError(`Erro ao deletar tarefa: ${error.message}`, error);
        }
    }

    async countPending(organizationId?: string): Promise<number> {
        let query = countPendingQuery();

        if (organizationId) {
            query = query.eq('organization_id', organizationId);
        }

        const { count, error } = await query;

        if (error) {
            console.error('[REPOSITORY] Error counting pending tasks:', error);
            return 0;
        }

        return count || 0;
    }

    async countOverdue(organizationId?: string): Promise<number> {
        let query = countOverdueQuery();

        if (organizationId) {
            query = query.eq('organization_id', organizationId);
        }

        const { count, error } = await query;

        if (error) {
            console.error('[REPOSITORY] Error counting overdue tasks:', error);
            return 0;
        }

        return count || 0;
    }
}

